const $_default =
{
    components()
    {
        return {};
    },

    options()
    {
        return [];
    },

    reduce( option )
    {
        return option?.id;
    },

    selectable( option )
    {
        return true;
    },

    getOptionLabel( option )
    {
        if( typeof option === 'object' )
        {
            if( !option.hasOwnProperty( this.label ) ) { return console.warn(`[vue-select warn]: Label key "option.${this.label}" does not exist in options object ${JSON.stringify(option)}.\n https://vue-select.org/api/props.html#getoptionlabel` ); }

            return option[ this.label ];
        }

        return option;
    },

    getOptionKey( option )
    {
        if( typeof option !== 'object' ) { return option }

        try
        {
            if( option.hasOwnProperty('id') )
            {
                return option.id;
            }
            else
            {
                const ordered = {};

                Object.keys( option ).sort().forEach((key) => { ordered[key] = option[key] });

                return JSON.stringify( ordered );
            }
        }
        catch( e )
        {
            const warning = `[vue-select warn]: Could not stringify this option to generate unique key. Please provide'getOptionKey' prop to return a unique key for each option.\n https://vue-select.org/api/props.html#getoptionkey`;

            return console.warn( warning, option, e )
        }
    },

    onTab()
    {
        if( this.selectOnTab && !this.isComposing )
        {
            this.typeAheadSelect();
        }
    },

    filterBy( option, label, search )
    {
        return ( ( label || '' ).toLocaleLowerCase().indexOf( search.toLocaleLowerCase() ) > -1 )
    },

    filter( options, search )
    {
        return options.filter( ( option ) =>
        {
            let label = this.getOptionLabel( option );

            if (typeof label === 'number') { label = label.toString(); }

            return this.filterBy( option, label, search );
        })
    },

    createOption( option )
    {
        return { [this.label]: option };
    },

    clearSearchOnBlur({ clearSearchOnSelect, multiple })
    {
        return clearSearchOnSelect && !multiple
    },

    mapKeydown( map, vm )
    {
        return map;
    },

    calculatePosition( dropdownList, component, { width, top, left, right, direction } )
    {
        /**
         * @param dropdownList {HTMLUListElement}
         * @param component {Vue} current instance of vue select
         * @param width {string} calculated width in pixels of the dropdown menu
         * @param top {string} absolute position top value in pixels relative to the document
         * @param left {string} absolute position left value in pixels relative to the document
         * @return {function|void}
         */

        /*
        dropdownList.style.top = top;
        dropdownList.style.left = left;
        dropdownList.style.width = width;
         */

        dropdownList.style.minWidth = width + 'px';
        dropdownList.style.left = left + 'px';
        dropdownList.style.top = top + 'px';

        if( !dropdownList.classList.contains( direction ) )
        {
            dropdownList.classList.remove('top', 'bottom');
            dropdownList.classList.add( direction );

            component.$el.classList.remove('top', 'bottom');
            component.$el.classList.add( direction );
        }

        dropdownList.style.top = top + ( direction === 'top' ? -1 : 1 ) + 'px';
    },

    dropdownShouldOpen({ noDrop, open, mutableLoading })
    {
        //return noDrop ? false : open && !mutableLoading;
        return noDrop ? false : open;
    },

    uid()
    {
        return Math.floor(Math.random() * Number.MAX_SAFE_INTEGER );
    }
}

const $_validator =
{
    resetOnOptionsChange: ( value ) =>
    {
        return ['function', 'boolean'].includes( typeof value )
    }
}

export default
{
    props:
    {
        modelValue              : {}, // Contains the currently selected value. Very similar to a `value` attribute on an <input>. You can listen for changes with the 'input' event.
        class                   : { type: String, default: '' },
        dropdownClass           : { type: String, default: '' },
        components              : { type: Object, default: $_default.components }, // An object with any custom components that you'd like to overwrite => https://vue-select.org/guide/components.html
        options                 : { type: Array, default: $_default.options }, // An array of strings or objects to be used as dropdown choices. => [{label: 'This is Foo', value: 'foo'}]
        disabled                : { type: Boolean, default: false }, // Disable the entire component.
        clearable               : { type: Boolean, default: true }, // Can the user clear the selected property.
        deselectFromDropdown    : { type: Boolean, default: true }, // Can the user deselect an option by clicking it from within the dropdown.
        searchable              : { type: Boolean, default: true }, // Enable/disable filtering the options.
        multiple                : { type: Boolean, default: false }, // Equivalent to the `multiple` attribute on a `<select>` input.
        placeholder             : { type: String, default: '' }, // Equivalent to the `placeholder` attribute on an `<input>`.
        transition              : { type: String, default: 'slide-fade' }, // Sets a Vue transition property on the `.vs__dropdown-menu`.
        clearSearchOnSelect     : { type: Boolean, default: true }, // Enables/disables clearing the search text when an option is selected.
        closeOnSelect           : { type: Boolean, default: true }, // Close a dropdown when an option is chosen. Set to false to keep the dropdown /  open (useful when combined with multi-select, for example)
        label                   : { type: String, default: 'label' }, // Tells vue-select what key to use when generating option labels when each `option` is an object.
        autocomplete            : { type: String, default: 'off' }, // Value of the 'autocomplete' field of the input element.
        reduce                  : { type: Function, default: $_default.reduce }, // When working with objects, the reduce prop allows you to transform a given object to only the information you want passed to a v-model binding or @input event.
        selectable              : { type: Function, default: $_default.selectable }, // Decides whether an option is selectable or not. Not selectable options are displayed but disabled and cannot be selected.
        getOptionLabel          : { type: Function, default: $_default.getOptionLabel }, // Callback to generate the label text. If {option} is an object, returns option[this.label] by default.
        getOptionKey            : { type: Function, default: $_default.getOptionKey }, // Generate a unique identifier for each option. If `option` is an object and `option.hasOwnProperty('id')` exists, `option.id` is used by default, otherwise the option will be serialized to JSON.
        onTab                   : { type: Function, default: $_default.onTab }, // Select the current value if selectOnTab is enabled - @deprecated since 3.3
        taggable                : { type: Boolean, default: false }, // Enable/disable creating options from searchEl.
        tabindex                : { type: Number, default: null }, // Set the tabindex for the input field.
        pushTags                : { type: Boolean, default: false }, // When true, newly created tags will be added to the options list.
        filterable              : { type: Boolean, default: true }, // When true, existing options will be filtered by the search text. Should not be used in conjunction with taggable.
        filterBy                : { type: Function, default: $_default.filterBy }, // Callback to determine if the provided option should match the current search text. Used to determine if the option should be displayed.
        filter                  : { type: Function, default: $_default.filter }, // Callback to filter results when search text is provided. Default implementation loops each option, and returns the result of this.filterBy.
        createOption            : { type: Function, default: $_default.createOption }, //User defined function for adding Options
        resetOnOptionsChange    : { default: false, validator: $_validator.resetOnOptionsChange }, // When false, updating the options will not reset the selected value. Accepts a `boolean` or `function` that returns a `boolean`. If defined as a function, it will receive the params listed below.
        clearSearchOnBlur       : { type: Function, default: $_default.clearSearchOnBlur }, // If search text should clear on blur
        noDrop                  : { type: Boolean, default: false }, // Disable the dropdown entirely.
        inputId                 : { type: String }, // Sets the id of the input element.
        dir                     : { type: String, default: 'auto' }, // Sets RTL support. Accepts 'ltr', 'rtl', 'auto'.
        selectOnTab             : { type: Boolean, default: false }, // When true, hitting the 'tab' key will select the current select value - @deprecated since 3.3 - use selectOnKeyCodes instead
        selectOnKeyCodes        : { type: Array, default: () => [13] }, // Keycodes that will select the current option.
        searchInputQuerySelector: { type: String, default: '[type=search]' }, // Query Selector used to find the search input when the 'search' scoped slot is used.
        mapKeydown              : { type: Function, default: $_default.mapKeydown }, // Used to modify the default keydown events map for the search input. Can be used to implement custom behaviour for key presses.
        appendToBody            : { type: Boolean, default: true }, // Append the dropdown element to the end of the body and size/position it dynamically. Use it if you have overflow or z-index issues. - http://vue-select.org/guide/positioning.html
        calculatePosition       : { type: Function, default: $_default.calculatePosition }, // When `appendToBody` is true, this function is responsible for positioning the drop down list.
        dropdownShouldOpen      : { type: Function, default: $_default.dropdownShouldOpen }, // Determines whether the dropdown should be open. Receives the component instance as the only argument.
        uid                     : { type: [ String, Number ], default: $_default.uid }, // A unique identifier used to generate IDs in HTML. Must be unique for every instance of the component.

        loading                 : { type: Boolean, default: false }, // Toggles the adding of a 'loading' class to the main .v-select wrapper. Useful to control UI state when results are being processed through AJAX.
        autoscroll              : { type: Boolean, default: true }, // Mixin from pointerScroll

        hideClearButton         : { type: Boolean, default: false }, // Show the clear button when the input is dirty
        hideIndicator           : { type: Boolean, default: false }, // Show the open indicator
    }
}